'use client';

import React, { useCallback, memo } from 'react';
import {
  AutoSelection,
  Box,
  EmptySearchResult,
  Icon,
  InlineStack,
  Listbox,
  Popover,
  Scrollable,
  Text,
  TextField,
  BlockStack,
  Thumbnail,
} from '@shopify/polaris';
import { SearchIcon, XIcon, SelectIcon } from '@shopify/polaris-icons';
import { Product } from '@/types/product';
import useProductSelect from '@/hooks/useProductSelect';
import { cn } from '@/lib/utils';

interface SimpleProductFilterProps {
  selectedProduct: Product | null;
  onChange: (product: Product | null) => void;
  bold?: boolean;
}

const ProductFilter = ({ selectedProduct, onChange, bold }: SimpleProductFilterProps) => {
  const { open, setOpen, query, setQuery, options: products, isLoading, loadProducts } = useProductSelect();

  const handleToggleOpen = useCallback(() => {
    const isCurrentlyClosed = !open;
    if (isCurrentlyClosed) {
      loadProducts();
    }
    setOpen(isCurrentlyClosed);
  }, [open, setOpen, loadProducts]);

  const handleProductSelect = useCallback(
    (productId: string) => {
      const product = products.find((p) => p.productId === productId);
      if (product) {
        const selectedProduct = {
          ...product,
          id: product.id || product.productId,
        };
        onChange(selectedProduct);
      }
      setOpen(false);
    },
    [onChange, products, setOpen]
  );

  const handleClearFilter = useCallback(() => {
    onChange(null);
  }, [onChange]);

  const getFormattedTitle = (title: string | undefined) => {
    if (!title) return '';
    return title.length > 22 ? title.substring(0, 19) + '...' : title;
  };

  const activator = (
    <BlockStack gap="200">
      <Text as={bold ? 'h3' : 'span'} variant={bold ? 'headingSm' : 'bodyMd'} tone={bold ? 'subdued' : 'base'}>
        Product
      </Text>
      <div
        onClick={handleToggleOpen}
        className="cursor-pointer"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggleOpen();
          }
        }}
      >
        <Box padding="100" borderColor="input-border" borderWidth="0165" borderRadius="200" minWidth="250px">
          <InlineStack gap="200" blockAlign="center" align="space-between">
            <InlineStack gap="200" blockAlign="center" wrap={false}>
              <div className={cn('flex h-6 items-center justify-center', selectedProduct?.imageUrl && 'w-6')}>
                {selectedProduct?.imageUrl && (
                  <Thumbnail source={selectedProduct.imageUrl} alt={selectedProduct.title || ''} size="extraSmall" />
                )}
              </div>
              <Text as="span" variant="bodyMd" truncate>
                {selectedProduct ? getFormattedTitle(selectedProduct.title) : 'Select a product'}
              </Text>
            </InlineStack>
            <InlineStack gap="100" blockAlign="center">
              {selectedProduct && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearFilter();
                  }}
                  className="cursor-pointer"
                >
                  <Icon source={XIcon} tone="subdued" />
                </div>
              )}
              <Icon source={SelectIcon} tone="subdued" />
            </InlineStack>
          </InlineStack>
        </Box>
      </div>
    </BlockStack>
  );

  return (
    <Popover
      active={open}
      activator={activator}
      autofocusTarget="first-node"
      onClose={() => setOpen(false)}
      preferredPosition="below"
      preferredAlignment="left"
    >
      <div style={{ minWidth: '250px' }}>
        <Box padding="200">
          <TextField
            autoComplete="off"
            label="Search products"
            labelHidden
            value={query}
            onChange={(value) => {
              setQuery(value);
              if (!value.trim()) {
                loadProducts();
              }
            }}
            clearButton
            onClearButtonClick={() => {
              setQuery('');
              loadProducts();
            }}
            prefix={<Icon source={SearchIcon} />}
            placeholder="Search products"
          />
        </Box>

        <Scrollable style={{ height: '180px' }} shadow>
          <Listbox
            enableKeyboardControl
            autoSelection={AutoSelection.FirstSelected}
            accessibilityLabel="Search for and select a product"
            onSelect={handleProductSelect}
          >
            {isLoading && <Listbox.Loading accessibilityLabel="Loading products" />}

            {!isLoading && products.length === 0 && (
              <EmptySearchResult
                title=""
                description={query ? `No products found matching "${query}"` : 'No products found'}
              />
            )}

            {!isLoading &&
              products.length > 0 &&
              products.slice(0, 10).map(({ productId, title, imageUrl }) => {
                const selected = productId === selectedProduct?.productId || productId === selectedProduct?.id;
                const displayTitle = getFormattedTitle(title);

                return (
                  <Listbox.Option key={productId} value={productId!} selected={selected}>
                    <Listbox.TextOption selected={selected}>
                      <Box padding="100">
                        <InlineStack gap="200" blockAlign="center" wrap={false}>
                          {imageUrl ? (
                            <Box width="20px">
                              <Thumbnail source={imageUrl} alt={displayTitle} size="small" />
                            </Box>
                          ) : null}
                          <Box maxWidth="180px">
                            <Text as="span" variant="bodySm" truncate>
                              {displayTitle}
                            </Text>
                          </Box>
                        </InlineStack>
                      </Box>
                    </Listbox.TextOption>
                  </Listbox.Option>
                );
              })}
          </Listbox>
        </Scrollable>
      </div>
    </Popover>
  );
};

ProductFilter.displayName = 'ProductFilter';

export default memo(ProductFilter);
